import { AxiosError } from 'axios';
import { OfflineError } from '@/api/client';

/**
 * Pulls a user-facing message out of an unknown error.
 *
 * Order of preference:
 *   1. OfflineError → its own canned copy
 *   2. AxiosError with a server response → `response.data.error` if the
 *      server sent one (the convention used by every route in this repo).
 *   3. AxiosError with no response → "Network error — check your
 *      connection". The default `err.message` here is "Request failed
 *      with status code …", which is jargon to most users.
 *   4. Other Error subclasses → their `message` if non-empty
 *   5. Anything else → the caller-supplied fallback
 *
 * The fallback exists for unexpected error shapes (rejection with a
 * string, throw of a plain object, etc.) — it should describe the
 * action that failed in plain English ("Couldn't save the user").
 */
export function extractErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof OfflineError) return err.message;
    if (err instanceof AxiosError) {
        const serverMsg = err.response?.data?.error;
        if (typeof serverMsg === 'string' && serverMsg.trim()) return serverMsg;
        if (!err.response) return 'Network error — please check your connection and try again.';
        return fallback;
    }
    if (err instanceof Error && err.message) return err.message;
    return fallback;
}
